"use client";

import React, { useEffect, useState } from "react";
import { motion, useAnimation } from "framer-motion";
import Image from "next/image";
import { MobileWorkItemProps } from "./types";

const MobileWorkItem: React.FC<MobileWorkItemProps> = ({
  src,
  alt,
  title,
  description,
  index,
}) => {
  const controls = useAnimation();
  const [hasAnimated, setHasAnimated] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const element = document.getElementById(`work-item-${index}`);
      if (!element) return;
      const rect = element.getBoundingClientRect();
      const isVisible = rect.top <= window.innerHeight * 0.75;
      if (isVisible && !hasAnimated) {
        controls.start("visible");
        setHasAnimated(true);
      } else if (!isVisible && hasAnimated) {
        controls.start("hidden");
        setHasAnimated(false);
      }
    };
    window.addEventListener("scroll", handleScroll);
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, [controls, hasAnimated, index]);

  const containerVariants = {
    hidden: { opacity: 0.5 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.4,
        staggerChildren: 0.2,
      },
    },
  };

  const imageVariants = {
    hidden: { scale: 0.95, opacity: 0.7 },
    visible: {
      scale: 1,
      opacity: 1,
      transition: { duration: 0.5 },
    },
  };

  const textVariants = {
    hidden: { y: 30, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        damping: 20,
        stiffness: 100,
      },
    },
  };

  const isEven = index % 2 === 0;

  return (
    <motion.div
      id={`work-item-${index}`}
      className="w-full"
      variants={containerVariants}
      initial="hidden"
      animate={controls}
    >
      {/* Image Container */}
      <motion.div
        className={`relative w-full h-[230px] overflow-hidden rounded-tl-lg rounded-tr-lg ${
          isEven ? "mr-auto" : "ml-auto"
        }`}
        variants={imageVariants}
      >
        <Image src={src} alt={alt} fill className="object-cover" />
      </motion.div>

      {/* Text Container */}
      <motion.div
        className="bg-[rgba(65,59,59,0.7)] backdrop-blur-sm text-white p-5 px-4 rounded-bl-lg rounded-br-lg"
        variants={textVariants}
      >
        <h3 className="text-xl font-medium mb-2">{title}</h3>
        <p className="font-light text-justify">{description}</p>
      </motion.div>
    </motion.div>
  );
};

export default MobileWorkItem;
